import type { Direction } from '@veriverse/game-logic';
import { CHARACTER_IDS, premadeNumber, type CharacterId } from '@veriverse/shared';
import type Phaser from 'phaser';
import { prefersReducedMotion } from '../state/settings.js';

const PREMADE_SHEETS = import.meta.glob<string>(
  '../../../../tile_assets/04_Characters/Character_Generator/0_Premade_Characters/16x16/*.png',
  { eager: true, query: '?url', import: 'default' },
);
const NAMED_SHEETS = import.meta.glob<string>(
  '../../../../tile_assets/04_Characters/Old/Single_Characters_Legacy/16x16/*_16x16.png',
  { eager: true, query: '?url', import: 'default' },
);

/** File name of a character's sheet: `Premade_Character_07.png`, `Conference_man_16x16.png`. */
export function sheetFileName(id: CharacterId): string {
  const number = premadeNumber(id);
  if (number !== undefined) return `Premade_Character_${String(number).padStart(2, '0')}.png`;
  return `${id.charAt(0).toUpperCase()}${id.slice(1)}_16x16.png`;
}

/**
 * Premade characters from LimeZu's "Modern Interiors" pack (assets-src/LICENSES.md): one sheet
 * per character, 16 px art in 16×32 frames. Row 1 is idle and row 2 walking, each with 6 frames
 * per direction in the order right, up, left, down. The figure fills the bottom 24 rows, so drawn
 * at 2× it is exactly a 32×48 character (style guide §5, O10) standing on its tile.
 */
export type CharacterAnimation = 'idle' | 'walk';

/** URL of each character's sheet; absent if the file is missing (the character then can't load). */
export const CHARACTER_SHEETS: Readonly<Partial<Record<CharacterId, string>>> = Object.fromEntries(
  CHARACTER_IDS.flatMap((id) => {
    const name = sheetFileName(id);
    const url = Object.entries(
      premadeNumber(id) === undefined ? NAMED_SHEETS : PREMADE_SHEETS,
    ).find(([path]) => path.endsWith(`/${name}`))?.[1];
    return url ? [[id, url]] : [];
  }),
);

/** Sheet row of each animation. */
export const SHEET_ROWS: Readonly<Record<CharacterAnimation, number>> = { idle: 1, walk: 2 };

export const SHEET_FRAME = { width: 16, height: 32 } as const;
/** Integer scale from the 16 px art to the game's 32 px tiles. */
export const SHEET_SCALE = 2;
/** Empty rows above the figure in every frame (the figure is the bottom 24 rows). */
export const SHEET_TOP_PADDING = 8;
export const FRAMES_PER_DIRECTION = 6;
const SHEET_DIRECTIONS: readonly Direction[] = ['right', 'up', 'left', 'down'];

/** Style guide §5.1: idle 6 fps, walk 12 fps (3 × tiles per second), both looping. */
const FRAME_RATES: Readonly<Record<CharacterAnimation, number>> = { idle: 6, walk: 12 };

/** Texture key of a character's sheet. */
export const sheetKey = (id: CharacterId): string => `character:${id}`;

/** Animation key, named like the style guide's Aseprite tags: `walk_down`, `idle_left`. */
export const animationKey = (
  id: CharacterId,
  animation: CharacterAnimation,
  direction: Direction,
): string => `character:${id}:${animation}_${direction}`;

/** Column of the first frame of a direction in an animation's row. */
export function firstFrame(direction: Direction): number {
  return SHEET_DIRECTIONS.indexOf(direction) * FRAMES_PER_DIRECTION;
}

/** Last row of the hips in the back view; the legs below it are hidden by the seat. */
const HIP_ROW = 27;
/** Last row of the head in the back view; the shoulders start below it. */
const NECK_ROW = 20;
/** First row of the hands hanging at the sides (the outer 3 columns on each side). */
const HAND_ROW = 22;
const HAND_WIDTH = 3;
/** How far the seated body sits lower than the standing one, in source pixels. */
const SEAT_DROP = SHEET_FRAME.height - 1 - HIP_ROW;

/**
 * Typing at the desk, seen from behind (style guide §5.1 sit-typing: 4 frames at 8 fps, looping):
 * at rest, then the head dips a pixel while the left hand reaches up, at rest, then the same with
 * the right hand, like the people at their computers in LimeZu's office designs.
 */
const TYPING_FRAMES: readonly { readonly headDip: number; readonly hand?: 'left' | 'right' }[] = [
  { headDip: 0 },
  { headDip: 1, hand: 'left' },
  { headDip: 0 },
  { headDip: 1, hand: 'right' },
];
const TYPING_RATE = 8;

export const seatedAnimationKey = (id: CharacterId): string => `character:${id}:sit_typing_up`;

/** How far the head sits below its standing position in a seated frame, in source pixels. */
export const seatedHeadDrop = (frame: number): number =>
  SEAT_DROP + (TYPING_FRAMES[frame]?.headDip ?? 0);

/**
 * An offline colleague at their desk, seen from behind (the seat is the tile below the desk, so
 * they face up), typing away: offline is taken to mean working. The pack has no such pose, so it
 * is made from the back view of the idle row: the body down to the hips, lowered onto the
 * seat (the legs are hidden), in the frames of `TYPING_FRAMES`. With reduced motion it holds the
 * first frame. Returns the animation key, or `undefined` if the sheet is not loaded.
 */
export function ensureSeatedAnimation(scene: Phaser.Scene, id: CharacterId): string | undefined {
  const key = seatedAnimationKey(id);
  if (scene.anims.exists(key)) return key;
  if (!scene.textures.exists(sheetKey(id))) return undefined;
  const source = scene.textures.get(sheetKey(id)).getSourceImage() as CanvasImageSource;
  const { width, height } = SHEET_FRAME;
  const canvas = scene.textures.createCanvas(key, width * TYPING_FRAMES.length, height);
  if (!canvas) return undefined;
  const ctx = canvas.getContext();
  const sx = firstFrame('up') * width;
  const sy = SHEET_ROWS.idle * height;
  // Copies source rows [fromRow, toRow] of columns [col, col + cols) to the seated position.
  const part = (x: number, col: number, cols: number, fromRow: number, toRow: number, dy = 0) =>
    ctx.drawImage(
      source,
      sx + col,
      sy + fromRow,
      cols,
      toRow - fromRow + 1,
      x + col,
      fromRow + SEAT_DROP + dy,
      cols,
      toRow - fromRow + 1,
    );
  TYPING_FRAMES.forEach((frame, index) => {
    const x = index * width;
    part(x, 0, width, NECK_ROW + 1, HIP_ROW);
    if (frame.hand) {
      const col = frame.hand === 'left' ? 0 : width - HAND_WIDTH;
      ctx.clearRect(x + col, HAND_ROW + SEAT_DROP, HAND_WIDTH, HIP_ROW - HAND_ROW + 1);
      part(x, col, HAND_WIDTH, HAND_ROW, HIP_ROW, -1);
    }
    part(x, 0, width, SHEET_TOP_PADDING, NECK_ROW, frame.headDip);
    canvas.add(index, 0, x, 0, width, height);
  });
  canvas.refresh();
  const reducedMotion = prefersReducedMotion();
  scene.anims.create({
    key,
    frames: (reducedMotion ? [0] : TYPING_FRAMES.map((_, index) => index)).map((frame) => ({
      key,
      frame,
    })),
    frameRate: TYPING_RATE,
    repeat: -1,
  });
  return key;
}

export function loadCharacterSheets(scene: Phaser.Scene): void {
  for (const id of CHARACTER_IDS) {
    const url = CHARACTER_SHEETS[id];
    if (!url) continue;
    scene.load.spritesheet(sheetKey(id), url, {
      frameWidth: SHEET_FRAME.width,
      frameHeight: SHEET_FRAME.height,
    });
  }
}

/**
 * One looping animation per character, animation and direction. With reduced motion the idle
 * loop holds its first frame (style guide §5.1); walking still animates, as the figure moves.
 */
export function createCharacterAnimations(scene: Phaser.Scene, reducedMotion: boolean): void {
  for (const id of CHARACTER_IDS) {
    if (!scene.textures.exists(sheetKey(id))) continue;
    // Sheets differ in width, so frame numbers (row-major) depend on the number of columns.
    const image = scene.textures.get(sheetKey(id)).getSourceImage();
    const columns = Math.floor(image.width / SHEET_FRAME.width);
    for (const animation of ['idle', 'walk'] as const) {
      for (const direction of SHEET_DIRECTIONS) {
        const key = animationKey(id, animation, direction);
        if (scene.anims.exists(key)) continue;
        const start = SHEET_ROWS[animation] * columns + firstFrame(direction);
        const still = animation === 'idle' && reducedMotion;
        scene.anims.create({
          key,
          frames: scene.anims.generateFrameNumbers(sheetKey(id), {
            start,
            end: still ? start : start + FRAMES_PER_DIRECTION - 1,
          }),
          frameRate: FRAME_RATES[animation],
          repeat: -1,
        });
      }
    }
  }
}
